// frontend/src/api/equipmentCatalog.ts

import { api } from './client';
import type {
  EquipmentViewerResponse,
  EquipmentViewerFilters,
  CatalogCategoriesResponse,
  CatalogBrandsResponse,
  EquipmentProductWithSources,
  UnmergeEquipmentRequest,
  UnmergeEquipmentResponse,
  MergeEquipmentRequest,
  MergeEquipmentResponse,
} from '@/types/equipmentCatalog';

export const equipmentCatalogApi = {
  /**
   * Get categories with subcategories for filtering.
   */
  async getCategories(): Promise<CatalogCategoriesResponse> {
    return api.get<CatalogCategoriesResponse>('/api/equipment/catalog/categories');
  },

  /**
   * Get brands, optionally filtered by subcategory or category.
   */
  async getBrands(subcategoryId?: string, categoryId?: string): Promise<CatalogBrandsResponse> {
    const params = new URLSearchParams();
    if (subcategoryId) params.append('subcategory_id', subcategoryId);
    if (categoryId) params.append('category_id', categoryId);
    const queryString = params.toString();
    return api.get<CatalogBrandsResponse>(
      `/api/equipment/catalog/brands${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get viewer data with filters.
   */
  async getViewerData(filters?: EquipmentViewerFilters): Promise<EquipmentViewerResponse> {
    const params = new URLSearchParams();
    if (filters?.q) params.append('q', filters.q);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.subcategory) params.append('subcategory', filters.subcategory);
    if (filters?.brand) params.append('brand', filters.brand);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    return api.get<EquipmentViewerResponse>(
      `/api/equipment/catalog/viewer${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get a product with full source details for merge/unmerge operations.
   */
  async getProductWithSources(productId: string): Promise<EquipmentProductWithSources> {
    return api.get<EquipmentProductWithSources>(
      `/api/equipment/catalog/products/${encodeURIComponent(productId)}/sources`
    );
  },

  /**
   * Unmerge a source from a product, creating a new separate product.
   */
  async unmergeProduct(request: UnmergeEquipmentRequest): Promise<UnmergeEquipmentResponse> {
    return api.post<UnmergeEquipmentResponse>('/api/equipment/catalog/unmerge', request);
  },

  /**
   * Merge two products into one.
   */
  async mergeProducts(request: MergeEquipmentRequest): Promise<MergeEquipmentResponse> {
    return api.post<MergeEquipmentResponse>('/api/equipment/catalog/merge', request);
  },
};
